/**
 * Platform Admin - Usage & Audit Logs Page
 */

import React, { useEffect, useState, CSSProperties } from 'react';
import {
  platformClient,
  UsageLog,
  AuditLog,
  PlatformAuditLog,
  Organization,
  Pagination,
  LogsFilter,
} from '../api/platformClient';
import {
  Clock,
  FileText,
  Shield,
  ChevronLeft,
  ChevronRight,
  Filter,
  Download,
  RefreshCw,
} from 'lucide-react';

type LogType = 'usage' | 'audit' | 'platform-audit';

export default function LogsPage() {
  const [activeTab, setActiveTab] = useState<LogType>('usage');
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [platformAuditLogs, setPlatformAuditLogs] = useState<PlatformAuditLog[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filter, setFilter] = useState<LogsFilter>({
    page: 1,
    limit: 50,
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [activeTab, filter]);

  const loadOrganizations = async () => {
    try {
      const data = await platformClient.listOrganizations(1, 1000);
      setOrganizations(data.organizations);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  const loadLogs = async () => {
    setIsLoading(true);
    setError('');

    try {
      if (activeTab === 'usage') {
        const data = await platformClient.getUsageLogs(filter);
        setUsageLogs(data.logs);
        setPagination(data.pagination);
      } else if (activeTab === 'audit') {
        const data = await platformClient.getAuditLogs(filter);
        setAuditLogs(data.logs);
        setPagination(data.pagination);
      } else {
        const data = await platformClient.getPlatformAuditLogs(filter);
        setPlatformAuditLogs(data.logs);
        setPagination(data.pagination);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load logs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab: LogType) => {
    setActiveTab(tab);
    setFilter({ ...filter, page: 1 });
  };

  const handleFilterChange = (key: keyof LogsFilter, value: string) => {
    setFilter({ ...filter, [key]: value || undefined, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setFilter({ ...filter, page });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatMinutes = (minutes: number) => {
    if (minutes < 1) {
      return `${Math.round(minutes * 60)}s`;
    }
    return `${minutes.toFixed(1)}min`;
  };

  const exportCSV = () => {
    let csvContent = '';
    let filename = '';

    if (activeTab === 'usage') {
      csvContent = 'ID,Organization,Room,Minutes Used,Hours Before,Hours After,Started At,Ended At\n';
      usageLogs.forEach((log) => {
        csvContent += `${log.id},${log.organizationName || log.organizationId},${log.roomName || log.roomId || '-'},${log.minutesUsed},${log.hoursBefore},${log.hoursAfter},${log.startedAt},${log.endedAt}\n`;
      });
      filename = 'usage-logs.csv';
    } else if (activeTab === 'audit') {
      csvContent = 'ID,Organization,User,Action,Resource Type,Resource ID,Created At\n';
      auditLogs.forEach((log) => {
        csvContent += `${log.id},${log.organizationName || log.organizationId},${log.userEmail || log.userId || '-'},${log.action},${log.resourceType},${log.resourceId || '-'},${log.createdAt}\n`;
      });
      filename = 'audit-logs.csv';
    } else {
      csvContent = 'ID,Admin,Action,Target Type,Target ID,Created At\n';
      platformAuditLogs.forEach((log) => {
        csvContent += `${log.id},${log.adminEmail || log.platformAdminId || '-'},${log.action},${log.targetType},${log.targetId || '-'},${log.createdAt}\n`;
      });
      filename = 'platform-audit-logs.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Logs</h1>
        <div style={styles.headerActions}>
          <button onClick={() => setShowFilters(!showFilters)} style={styles.filterButton}>
            <Filter size={16} />
            Filters
          </button>
          <button onClick={exportCSV} style={styles.exportButton}>
            <Download size={16} />
            Export CSV
          </button>
          <button onClick={loadLogs} style={styles.refreshButton}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={activeTab === 'usage' ? styles.tabActive : styles.tab}
          onClick={() => handleTabChange('usage')}
        >
          <Clock size={16} />
          Usage Logs
        </button>
        <button
          style={activeTab === 'audit' ? styles.tabActive : styles.tab}
          onClick={() => handleTabChange('audit')}
        >
          <FileText size={16} />
          Audit Logs
        </button>
        <button
          style={activeTab === 'platform-audit' ? styles.tabActive : styles.tab}
          onClick={() => handleTabChange('platform-audit')}
        >
          <Shield size={16} />
          Platform Admin Logs
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div style={styles.filtersPanel}>
          {activeTab !== 'platform-audit' && (
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Organization</label>
              <select
                style={styles.filterSelect}
                value={filter.orgId || ''}
                onChange={(e) => handleFilterChange('orgId', e.target.value)}
              >
                <option value="">All Organizations</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {activeTab !== 'usage' && (
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Action</label>
              <input
                type="text"
                style={styles.filterInput}
                placeholder="Filter by action..."
                value={filter.action || ''}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              />
            </div>
          )}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Start Date</label>
            <input
              type="date"
              style={styles.filterInput}
              value={filter.startDate || ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>End Date</label>
            <input
              type="date"
              style={styles.filterInput}
              value={filter.endDate || ''}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div style={styles.loading}>Loading...</div>
      ) : error ? (
        <div style={styles.error}>{error}</div>
      ) : (
        <>
          {activeTab === 'usage' && (
            <UsageLogsTable logs={usageLogs} formatDate={formatDate} formatMinutes={formatMinutes} />
          )}
          {activeTab === 'audit' && (
            <AuditLogsTable logs={auditLogs} formatDate={formatDate} />
          )}
          {activeTab === 'platform-audit' && (
            <PlatformAuditLogsTable logs={platformAuditLogs} formatDate={formatDate} />
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={styles.pageButton}
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                <ChevronLeft size={16} />
              </button>
              <span style={styles.pageInfo}>
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <button
                style={styles.pageButton}
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Usage Logs Table
function UsageLogsTable({
  logs,
  formatDate,
  formatMinutes,
}: {
  logs: UsageLog[];
  formatDate: (d: string) => string;
  formatMinutes: (m: number) => string;
}) {
  if (logs.length === 0) {
    return <div style={styles.empty}>No usage logs found</div>;
  }

  return (
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Organization</th>
            <th style={styles.th}>Room</th>
            <th style={styles.th}>Duration</th>
            <th style={styles.th}>Hours Before</th>
            <th style={styles.th}>Hours After</th>
            <th style={styles.th}>Started</th>
            <th style={styles.th}>Ended</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id} style={styles.tr}>
              <td style={styles.td}>
                <div style={styles.cellPrimary}>{log.organizationName || '-'}</div>
                <div style={styles.cellSecondary}>{log.organizationSlug}</div>
              </td>
              <td style={styles.td}>{log.roomName || log.roomId || '-'}</td>
              <td style={styles.td}>
                <span style={styles.badge}>{formatMinutes(log.minutesUsed)}</span>
              </td>
              <td style={styles.td}>{log.hoursBefore.toFixed(2)}h</td>
              <td style={styles.td}>{log.hoursAfter.toFixed(2)}h</td>
              <td style={styles.td}>{formatDate(log.startedAt)}</td>
              <td style={styles.td}>{formatDate(log.endedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Audit Logs Table
function AuditLogsTable({
  logs,
  formatDate,
}: {
  logs: AuditLog[];
  formatDate: (d: string) => string;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (logs.length === 0) {
    return <div style={styles.empty}>No audit logs found</div>;
  }

  return (
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Organization</th>
            <th style={styles.th}>User</th>
            <th style={styles.th}>Action</th>
            <th style={styles.th}>Resource</th>
            <th style={styles.th}>IP Address</th>
            <th style={styles.th}>Created At</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <React.Fragment key={log.id}>
              <tr
                style={{ ...styles.tr, cursor: 'pointer' }}
                onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
              >
                <td style={styles.td}>
                  <div style={styles.cellPrimary}>{log.organizationName || '-'}</div>
                  <div style={styles.cellSecondary}>{log.organizationSlug}</div>
                </td>
                <td style={styles.td}>
                  <div style={styles.cellPrimary}>{log.userDisplayName || log.userEmail || '-'}</div>
                  {log.userEmail && log.userDisplayName && (
                    <div style={styles.cellSecondary}>{log.userEmail}</div>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={styles.actionBadge}>{log.action}</span>
                </td>
                <td style={styles.td}>
                  <div style={styles.cellPrimary}>{log.resourceType}</div>
                  <div style={styles.cellSecondary}>{log.resourceId || '-'}</div>
                </td>
                <td style={styles.td}>{log.ipAddress || '-'}</td>
                <td style={styles.td}>{formatDate(log.createdAt)}</td>
              </tr>
              {expandedId === log.id && (log.oldValue || log.newValue) && (
                <tr>
                  <td colSpan={6} style={styles.expandedCell}>
                    <div style={styles.expandedContent}>
                      {log.oldValue && (
                        <div style={styles.valueBlock}>
                          <strong>Old Value:</strong>
                          <pre style={styles.pre}>{JSON.stringify(log.oldValue, null, 2)}</pre>
                        </div>
                      )}
                      {log.newValue && (
                        <div style={styles.valueBlock}>
                          <strong>New Value:</strong>
                          <pre style={styles.pre}>{JSON.stringify(log.newValue, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Platform Audit Logs Table
function PlatformAuditLogsTable({
  logs,
  formatDate,
}: {
  logs: PlatformAuditLog[];
  formatDate: (d: string) => string;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (logs.length === 0) {
    return <div style={styles.empty}>No platform audit logs found</div>;
  }

  return (
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Admin</th>
            <th style={styles.th}>Action</th>
            <th style={styles.th}>Target</th>
            <th style={styles.th}>IP Address</th>
            <th style={styles.th}>Created At</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <React.Fragment key={log.id}>
              <tr
                style={{ ...styles.tr, cursor: 'pointer' }}
                onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
              >
                <td style={styles.td}>
                  <div style={styles.cellPrimary}>{log.adminDisplayName || log.adminEmail || '-'}</div>
                  {log.adminEmail && log.adminDisplayName && (
                    <div style={styles.cellSecondary}>{log.adminEmail}</div>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={styles.actionBadge}>{log.action}</span>
                </td>
                <td style={styles.td}>
                  <div style={styles.cellPrimary}>{log.targetType}</div>
                  <div style={styles.cellSecondary}>{log.targetId || '-'}</div>
                </td>
                <td style={styles.td}>{log.ipAddress || '-'}</td>
                <td style={styles.td}>{formatDate(log.createdAt)}</td>
              </tr>
              {expandedId === log.id && (log.oldValue || log.newValue) && (
                <tr>
                  <td colSpan={5} style={styles.expandedCell}>
                    <div style={styles.expandedContent}>
                      {log.oldValue && (
                        <div style={styles.valueBlock}>
                          <strong>Old Value:</strong>
                          <pre style={styles.pre}>{JSON.stringify(log.oldValue, null, 2)}</pre>
                        </div>
                      )}
                      {log.newValue && (
                        <div style={styles.valueBlock}>
                          <strong>New Value:</strong>
                          <pre style={styles.pre}>{JSON.stringify(log.newValue, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    background: '#fff',
    color: '#374151',
    fontSize: '14px',
    cursor: 'pointer',
  },
  exportButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '8px',
    background: '#22c55e',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '16px',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '0',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 20px',
    border: 'none',
    borderBottom: '2px solid transparent',
    background: 'transparent',
    color: '#6b7280',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: '-1px',
  },
  tabActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 20px',
    border: 'none',
    borderBottom: '2px solid #6366f1',
    background: 'transparent',
    color: '#6366f1',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: '-1px',
  },
  filtersPanel: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '16px',
    padding: '16px',
    background: '#f9fafb',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    minWidth: '200px',
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#6b7280',
  },
  filterSelect: {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    background: '#fff',
  },
  filterInput: {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '48px',
    color: '#6b7280',
  },
  error: {
    textAlign: 'center' as const,
    padding: '48px',
    color: '#dc2626',
    background: '#fef2f2',
    borderRadius: '8px',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '48px',
    color: '#6b7280',
    background: '#f9fafb',
    borderRadius: '8px',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    background: '#fff',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '14px',
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 16px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    fontWeight: 500,
    color: '#374151',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    borderBottom: '1px solid #e5e7eb',
  },
  td: {
    padding: '12px 16px',
    color: '#1f2937',
    verticalAlign: 'top' as const,
  },
  cellPrimary: {
    fontWeight: 500,
    color: '#1f2937',
  },
  cellSecondary: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '2px',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    background: '#dbeafe',
    color: '#1d4ed8',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
  },
  actionBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    background: '#f3e8ff',
    color: '#7c3aed',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    fontFamily: 'monospace',
  },
  expandedCell: {
    padding: 0,
    background: '#f9fafb',
  },
  expandedContent: {
    padding: '16px',
    display: 'flex',
    gap: '24px',
  },
  valueBlock: {
    flex: 1,
  },
  pre: {
    background: '#1f2937',
    color: '#f9fafb',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '12px',
    overflow: 'auto',
    maxHeight: '200px',
    margin: '8px 0 0 0',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginTop: '24px',
  },
  pageButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
  },
  pageInfo: {
    fontSize: '14px',
    color: '#6b7280',
  },
};
